import { Link } from 'react-router-dom';
import { BookCover } from '@/components/books/BookCover';
import { Chip } from '@/components/ui/Chip';
import { cn } from '@/lib/cn';
import { formatRelativeTime } from '@/lib/relative-time';
import type { BookLabelView, BookView } from '@/types/api';

/**
 * AdminBookTable — the main content of the admin dashboard's left column.
 * Matches DESIGN.md Artboard 3's "Book Table":
 *
 *   Cover thumb (40×56) | Title + Author | Genre & Topic chips | Status |
 *   Updated (relative) | Edit →
 *
 * The table renders all books (live + pending) per ADMIN_DASHBOARD.md §
 * "Book Table" — status filtering is an open follow-up. The Edit link opens
 * `/admin/books/:id` in a new tab because the dashboard's filter state is
 * URL-driven and we don't want the editor to clobber it on back navigation.
 */
export interface AdminBookTableProps {
  books: BookView[];
  /** Show the loading placeholder copy instead of rows. */
  loading?: boolean;
  /** Shown in the empty row when a completed fetch returned zero books. */
  emptyLabel?: string;
  className?: string;
}

const COLUMN_COUNT = 6;

export function AdminBookTable({
  books,
  loading = false,
  emptyLabel = 'No books found.',
  className,
}: AdminBookTableProps) {
  return (
    <div
      className={cn(
        'overflow-x-auto rounded-2xl border border-or-slate-200 bg-white shadow-sm',
        className,
      )}
    >
      <table className="w-full min-w-[640px] border-collapse text-left text-[13px] text-or-slate-700">
        <thead className="bg-or-slate-50 text-[11px] font-semibold uppercase tracking-wide text-or-slate-500">
          <tr>
            <th scope="col" className="px-4 py-3 font-semibold">
              Cover
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Title
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Labels
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Status
            </th>
            <th scope="col" className="px-4 py-3 font-semibold">
              Updated
            </th>
            <th scope="col" className="px-4 py-3 text-right font-semibold">
              <span className="sr-only">Edit</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <TablePlaceholderRow label="Loading books…" />
          ) : books.length === 0 ? (
            <TablePlaceholderRow label={emptyLabel} />
          ) : (
            books.map((book, index) => <AdminBookRow key={book.id} book={book} index={index} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

function AdminBookRow({ book, index }: { book: BookView; index: number }) {
  const labels = pickRowLabels(book);
  const updatedLabel = formatRelativeTime(book.updatedAt);
  return (
    <tr
      className={cn(
        'border-t border-or-slate-200 transition hover:bg-or-slate-50/70',
        // Match the DESIGN.md mock — the first row is tinted to make the
        // active row pop. We don't track selection here; the tint is purely
        // visual anchoring for the top of the table.
        index === 0 && 'bg-or-orange-50/60',
      )}
    >
      <td className="px-4 py-3 align-middle">
        <AdminBookThumb book={book} />
      </td>
      <td className="px-4 py-3 align-middle">
        <div className="font-display text-[14px] font-semibold text-or-slate-900 line-clamp-1">
          {book.title}
        </div>
        <div className="text-[12px] text-or-slate-500 line-clamp-1">
          {book.author ? `by ${book.author}` : 'Author unknown'}
        </div>
      </td>
      <td className="px-4 py-3 align-middle">
        <div className="flex flex-wrap gap-1.5">
          {labels.length === 0 ? (
            <span className="text-[12px] italic text-or-slate-400">No labels</span>
          ) : (
            labels.map((label, i) => (
              <Chip key={`${label.id}-${i}`} variant={i === 0 ? 'orange' : 'default'}>
                {label.name}
              </Chip>
            ))
          )}
        </div>
      </td>
      <td className="px-4 py-3 align-middle">
        <StatusChip status={book.status} />
      </td>
      <td className="px-4 py-3 align-middle whitespace-nowrap text-[12px] text-or-slate-600">
        {updatedLabel || '—'}
      </td>
      <td className="px-4 py-3 align-middle text-right">
        <Link
          to={`/admin/books/${book.id}`}
          target="_blank"
          rel="noreferrer"
          className="text-[13px] font-semibold text-or-orange-600 hover:text-or-orange-700"
        >
          Edit →
        </Link>
      </td>
    </tr>
  );
}

function TablePlaceholderRow({ label }: { label: string }) {
  return (
    <tr>
      <td
        colSpan={COLUMN_COUNT}
        className="px-4 py-10 text-center text-[13px] italic text-or-slate-500"
      >
        {label}
      </td>
    </tr>
  );
}

function AdminBookThumb({ book }: { book: BookView }) {
  if (book.bookCover) {
    return (
      <img
        src={book.bookCover}
        alt=""
        width={40}
        height={56}
        loading="lazy"
        className="h-[56px] w-[40px] rounded-md object-cover object-top shadow-sm"
      />
    );
  }
  return (
    <BookCover
      title={book.title}
      author={book.author}
      seed={book.id}
      w={40}
      h={56}
      className="h-[56px] w-[40px] shadow-sm"
    />
  );
}

function StatusChip({ status }: { status: string }) {
  // `status` is typed as `string` so the client doesn't break when the
  // server adds a new value (see types/api.ts). We pattern-match the two
  // known values and fall back to the default pill for anything unknown.
  if (status === 'live') return <Chip variant="live">Live</Chip>;
  if (status === 'pending') return <Chip variant="pending">Pending</Chip>;
  return <Chip>{status}</Chip>;
}

/**
 * Pull up to three labels to show on the row — prioritize genres (they
 * anchor the first chip with the brand orange variant), then topics.
 * Everything else lives in the editor, so the table stays skimmable.
 */
function pickRowLabels(book: BookView): BookLabelView[] {
  const merged: BookLabelView[] = [...book.genres, ...book.topics];
  return merged.slice(0, 3);
}
